import { useCallback, useEffect, useState } from 'react';
import { getOrCreateUnloggedId } from '@/infra/api/core/tracker';
import { getDupelandoOriginFlag } from '@/presentation/services/dupelando';
import { getMetadataClient } from '../../services/runtime';
import {
  getExternalLinkClickoutUrlWithMeta,
  GetExternalLinkClickoutUrlWithMeta,
  getStoreClickoutUrlWithMeta,
  GetStoreClickoutWithMetaParams,
  GetOfferClickoutWithMetaParams,
  getOfferClickoutUrlWithMeta,
  GetProductClickoutWithMetaParams,
  getProductClickoutUrlWithMeta,
  GetBlogClickoutWithMetaParams,
} from '../../services/urls';
import { buildUrlFromLocation } from '../../services/urls/common';
import { useCurrentUserCache } from '../useCurrentUserCache';

export enum RedirectSource {
  Deal = 'deal',
  Store = 'store',
  Url = 'url',
  Product = 'Product',
  Blog = 'blog',
}

type MappedParams = {
  [RedirectSource.Deal]: GetOfferClickoutWithMetaParams;
  [RedirectSource.Store]: GetStoreClickoutWithMetaParams;
  [RedirectSource.Url]: GetExternalLinkClickoutUrlWithMeta;
  [RedirectSource.Product]: GetProductClickoutWithMetaParams;
  [RedirectSource.Blog]: GetBlogClickoutWithMetaParams;
};

type MappedReturns<Source extends RedirectSource> =
  Source extends RedirectSource.Url ? string : string | null;

const clickoutFunctionBySource = <Source extends RedirectSource>(
  source: Source,
  data:
    | MappedParams[RedirectSource]
    | MappedParams[RedirectSource.Store]
    | MappedParams[RedirectSource.Url]
) => {
  const getUrlClickoutOptions = {
    [RedirectSource.Deal]: () =>
      getOfferClickoutUrlWithMeta(data as MappedParams[RedirectSource.Deal]),
    [RedirectSource.Store]: () =>
      getStoreClickoutUrlWithMeta(data as MappedParams[RedirectSource.Store]),
    [RedirectSource.Url]: () =>
      getExternalLinkClickoutUrlWithMeta(
        data as MappedParams[RedirectSource.Url]
      ),
    [RedirectSource.Product]: () =>
      getProductClickoutUrlWithMeta(
        data as MappedParams[RedirectSource.Product]
      ),
    [RedirectSource.Blog]: () =>
      getProductClickoutUrlWithMeta(data as MappedParams[RedirectSource.Blog]),
  };
  return getUrlClickoutOptions[source] as () => MappedReturns<typeof source>;
};

export type UseClickoutUrl = <Source extends RedirectSource = RedirectSource>(
  source: Source,
  metadata: Partial<MappedParams[Source]> | Record<string, string>,
  userId: string
) => {
  clickoutUrl: MappedReturns<Source>;
  getClickoutUrl: (meta: typeof metadata) => MappedReturns<Source>;
};

export type GetProductClickoutUrl = (
  meta: Partial<GetProductClickoutWithMetaParams>
) => string | null;

const useClickoutUrl: UseClickoutUrl = (source, metadata, userId) => {
  const [location, setLocation] = useState({} as Location);
  const [isHydrated, setIsHydrated] = useState(false);
  const { isLogged } = useCurrentUserCache();

  const currentRelativeUrl = buildUrlFromLocation('', {
    pathname: location?.pathname || '',
    hash: location?.hash || '',
    search: location?.search || '',
  });

  useEffect(() => {
    setIsHydrated(true);
    setLocation(window.location);
  }, []);

  const getClickoutUrl = useCallback(
    (meta: typeof metadata) => {
      const newMeta = {
        ...metadata,
        ...meta,
        userId,
        client: isHydrated ? getMetadataClient() : undefined,
        currentRelativeUrl,
        guestId: getOrCreateUnloggedId(),
        came_from_dpl: getDupelandoOriginFlag(),
      };

      return clickoutFunctionBySource(source, newMeta)();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metadata, source, currentRelativeUrl, isHydrated, userId, isLogged]
  );

  const [clickoutUrl, setClickoutUrl] = useState(() => getClickoutUrl({}));

  useEffect(() => {
    setClickoutUrl(getClickoutUrl({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getClickoutUrl, isLogged]);

  return { clickoutUrl, getClickoutUrl };
};

export default useClickoutUrl;
